import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
  TableOfContentsRenderer,
  TableOfContentsSection,
} from '../components/templates/TableOfContentsRenderer';
import { EquipmentModel } from '../data/types';
import { ScreenHeading } from '../components/molecules/ScreenHeading';
import { ScreenSkeleton } from '../components/atoms/ScreenSkeleton';
import { EquipmentType, TaskStatus } from '../data/enums';
import { Button } from '../components/atoms/Button';
import { ArrowLeft } from 'lucide-react';
import useInspectionCategoryType from '../hooks/useInspectionCategoryType';
import { useAssetMineLocationLevel } from '../hooks/useAssetMineLocationLevel';
import { ErrorBanner } from '../components/atoms/ErrorBanner';
import { useInspectionCached } from '../hooks/useInspectionCached';
import useInspectionUndercarriageTableOfContentsIndicator from '../hooks/progressIndicators/useInspectionUndercarriageTableOfContentsIndicator';

export const UndercarriageSideTableOfContentsScreen = () => {
  const { externalId, sideName } = useParams();

  const [equipment, setEquipment] = useState<EquipmentModel | undefined>(
    undefined,
  );
  const [error, setError] = useState<string | null>(null);

  const undercarriageCategory = useInspectionCategoryType({
    inspectionId: externalId ?? '',
  });

  const navigate = useNavigate();

  const assetMineLocationState = useAssetMineLocationLevel({
    inspectionId: externalId ?? '',
  });

  const { inspection } = useInspectionCached(externalId ?? '');

  const {
    isReady: isUndercarriageReady,
    sideFrameIndicator,
    pitchExtensionIndicator,
    shoeRollerPathIndicator,
    shoeDriveLugIndicator,
    tumblerRollerPathIndicator,
    tumblerDriveLugIndicator,
    engagementIndicator,
    frontIdlerIndicator,
    rearIdlerIndicator,
    loadRollersIndicator,
    returnRollersIndicator,
    slidersIndicator,
  } = useInspectionUndercarriageTableOfContentsIndicator(
    externalId ?? '',
    equipment ?? undefined,
  );

  const [sections, setSections] = useState<TableOfContentsSection[]>([]);

  useEffect(() => {
    if (assetMineLocationState.isLoading) {
      return;
    }

    if (assetMineLocationState.error) {
      setError(assetMineLocationState.error);
      return;
    }

    if (!assetMineLocationState.asset) {
      setError('Asset not found');
      return;
    }

    const equipments = assetMineLocationState.asset?.equipments.filter(
      (e) =>
        e.equipmentCategory.toLowerCase() ===
        EquipmentType.undercarriage.toLowerCase(),
    );

    const sideNameDefined = sideName
      ?.split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
    const selectedSideEquipment = equipments?.find(
      (e) => e.name === sideNameDefined,
    );

    setEquipment(selectedSideEquipment);
  }, [
    assetMineLocationState.isLoading,
    assetMineLocationState.error,
    assetMineLocationState.asset,
  ]);

  const generateTooltip = (key: string) =>
    `undercarriage-wear-condition-heading-${key}`;

  useEffect(() => {
    const initInspectionData = async () => {
      if (inspection && inspection?.asset) {
        const sections: TableOfContentsSection[] = [
          {
            id: 'photos',
            order: 1,
            title: 'Photos',
            tooltipKey: generateTooltip('photos'),
            items: [
              {
                id: 'side-frame',
                order: 1,
                label: 'Side Frame',
                status: isUndercarriageReady
                  ? sideFrameIndicator
                  : TaskStatus.isLoading,
                enabled: true,
                url: 'side-frame',
                inspectionId: externalId ?? '',
                inspectionCategoryTypeId:
                  undercarriageCategory?.externalId ?? '',
              },
            ],
          },
          {
            id: 'shoes',
            order: 2,
            title: 'Shoes',
            tooltipKey: generateTooltip('shoes'),
            items: [
              {
                id: 'shoes-pitch-extension',
                order: 1,
                label: 'Pitch Extension',
                status: isUndercarriageReady
                  ? pitchExtensionIndicator
                  : TaskStatus.isLoading,
                enabled: true,
                url: 'shoes/pitch-extension',
                inspectionId: externalId ?? '',
                inspectionCategoryTypeId:
                  undercarriageCategory?.externalId ?? '',
              },
              {
                id: 'shoes-roller-path',
                order: 2,
                label: 'Roller Path',
                status: isUndercarriageReady
                  ? shoeRollerPathIndicator
                  : TaskStatus.isLoading,
                enabled: true,
                url: 'shoes/roller-path',
                inspectionId: externalId ?? '',
                inspectionCategoryTypeId:
                  undercarriageCategory?.externalId ?? '',
              },
              {
                id: 'shoes-drive-lug',
                order: 3,
                label: 'Drive Lug',
                status: isUndercarriageReady
                  ? shoeDriveLugIndicator
                  : TaskStatus.isLoading,
                enabled: true,
                url: 'shoes/drive-lug',
                inspectionId: externalId ?? '',
                inspectionCategoryTypeId:
                  undercarriageCategory?.externalId ?? '',
              },
            ],
          },
        ];

        //Drive Tumbler
        sections.push({
          id: 'drive-tumbler',
          order: 3,
          title: 'Drive tumbler',
          tooltipKey: generateTooltip('drive-tumbler'),
          items: [],
        });

        if (
          inspection.asset.model.hasTumblerRollPathMeasurement === true ||
          inspection.asset.model.assetType === 'HEX'
        ) {
          sections
            .find((section) => section.id === 'drive-tumbler')
            ?.items.push({
              id: 'drive-tumbler-roller-path',
              order: 1,
              label: 'Roller Path',
              status: isUndercarriageReady
                ? (tumblerRollerPathIndicator ?? TaskStatus.notStarted)
                : TaskStatus.isLoading,
              enabled: true,
              url: 'drive-tumbler/roller-path',
              inspectionId: externalId ?? '',
              inspectionCategoryTypeId: undercarriageCategory?.externalId ?? '',
            });
        }

        sections
          .find((section) => section.id === 'drive-tumbler')
          ?.items.push(
            {
              id: 'drive-tumbler-drive-lug',
              order: 2,
              label: 'Drive Lug',
              status: isUndercarriageReady
                ? tumblerDriveLugIndicator
                : TaskStatus.isLoading,
              enabled: true,
              url: 'drive-tumbler/drive-lug',
              inspectionId: externalId ?? '',
              inspectionCategoryTypeId: undercarriageCategory?.externalId ?? '',
            },
            {
              id: 'drive-tumbler-engagement',
              order: 3,
              label: 'Engagement',
              status: isUndercarriageReady
                ? engagementIndicator
                : TaskStatus.isLoading,
              enabled: true,
              url: 'drive-tumbler/engagement',
              inspectionId: externalId ?? '',
              inspectionCategoryTypeId: undercarriageCategory?.externalId ?? '',
            },
          );

        //Front Idler
        if (inspection.asset.model.showFrontIdlerConditionMenu === true) {
          sections.push({
            id: 'idler',
            order: 4,
            title: 'Idler',
            tooltipKey: generateTooltip('idler'),
            items: [
              {
                id: 'idler-front',
                order: 1,
                label: 'Front',
                status: isUndercarriageReady
                  ? (frontIdlerIndicator ?? TaskStatus.notStarted)
                  : TaskStatus.isLoading,
                enabled: true,
                url: 'idler/front',
                inspectionId: externalId ?? '',
                inspectionCategoryTypeId:
                  undercarriageCategory?.externalId ?? '',
              },
            ],
          });
        }

        //Roller Assemblies
        if (
          inspection.asset.model.showLoadRollerConditionMenu ||
          inspection.asset.model.showReturnRollerConditionMenu ||
          inspection.asset.model.showRearIdlerConditionMenu
        ) {
          sections.push({
            id: 'roller-assemblies',
            order: 5,
            title: 'Roller assemblies',
            tooltipKey: generateTooltip('roller-assemblies'),
            items: [],
          });
        }

        if (inspection.asset.model.showLoadRollerConditionMenu) {
          sections
            .find((section) => section.id === 'roller-assemblies')
            ?.items.push({
              id: 'roller-assemblies-load-rollers',
              order: 1,
              label: 'Load Rollers',
              status: isUndercarriageReady
                ? (loadRollersIndicator ?? TaskStatus.notStarted)
                : TaskStatus.isLoading,
              enabled: true,
              url: 'roller-assemblies/load-rollers',
              inspectionId: externalId ?? '',
              inspectionCategoryTypeId: undercarriageCategory?.externalId ?? '',
            });
        }

        if (inspection.asset.model.showReturnRollerConditionMenu) {
          sections
            .find((section) => section.id === 'roller-assemblies')
            ?.items.push({
              id: 'roller-assemblies-return-rollers',
              order: 2,
              label: 'Return Rollers',
              status: isUndercarriageReady
                ? (returnRollersIndicator ?? TaskStatus.notStarted)
                : TaskStatus.isLoading,
              enabled: true,
              url: 'roller-assemblies/return-rollers',
              inspectionId: externalId ?? '',
              inspectionCategoryTypeId: undercarriageCategory?.externalId ?? '',
            });
        }

        if (inspection.asset.model.showRearIdlerConditionMenu) {
          sections
            .find((section) => section.id === 'roller-assemblies')
            ?.items.push({
              id: 'roller-assemblies-rear-idler',
              order: 3,
              label: 'Rear',
              status: isUndercarriageReady
                ? (rearIdlerIndicator ?? TaskStatus.notStarted)
                : TaskStatus.isLoading,
              enabled: true,
              url: 'idler/rear',
              inspectionId: externalId ?? '',
              inspectionCategoryTypeId: undercarriageCategory?.externalId ?? '',
            });
        }

        if (inspection.asset.model.showSliderConditionMenu) {
          sections.push({
            id: 'slider',
            order: 6,
            title: 'Slider',
            tooltipKey: generateTooltip('slider'),
            items: [
              {
                id: 'slider-slider',
                order: 1,
                label: 'Slider',
                status: isUndercarriageReady
                  ? (slidersIndicator ?? TaskStatus.notStarted)
                  : TaskStatus.isLoading,
                enabled: true,
                url: 'sliders/sliders',
                inspectionId: externalId ?? '',
                inspectionCategoryTypeId:
                  undercarriageCategory?.externalId ?? '',
              },
            ],
          });
        }

        setSections(sections);
      }
    };

    initInspectionData();
  }, [
    inspection,
    sideFrameIndicator,
    pitchExtensionIndicator,
    shoeRollerPathIndicator,
    shoeDriveLugIndicator,
    tumblerRollerPathIndicator,
    tumblerDriveLugIndicator,
    engagementIndicator,
    frontIdlerIndicator,
    rearIdlerIndicator,
    undercarriageCategory?.externalId,
    externalId,
    equipment,
    loadRollersIndicator,
    returnRollersIndicator,
    slidersIndicator,
    isUndercarriageReady,
  ]);

  if (!inspection || (!sideName && assetMineLocationState.isLoading)) {
    // Layout stability: reserve realistic screen space instead of a zero-height body
    // with a floating spinner - the old idiom landed the full tile list as one giant
    // layout shift on every navigation.
    return <ScreenSkeleton fields={8} />;
  }

  if (error) {
    return <ErrorBanner message={error} />;
  }

  return (
    <div>
      <ScreenHeading
        sticky
        title={
          sideName
            ? `Undercarriage ${sideName.replace('-', ' ')}`
            : 'Undercarriage'
        }
        tag={sideName ? sideName.split('-')[0] : ''}
        tooltipKey={'undercarriage-wear-condition-heading-screen-header'}
      />
      {/* ToC Content */}
      <TableOfContentsRenderer
        sections={sections}
        header={null}
        footer={
          <div className="flex flex-col items-center justify-center space-y-4 px-4">
            <Button
              variant="secondary"
              onClick={() => navigate(-1)}
              className="mt-8"
            >
              <ArrowLeft size={24} />
              Done
            </Button>
          </div>
        }
      />
    </div>
  );
};
